/* eslint-disable class-methods-use-this */
import { GraphQLException } from 'exceptions';
import stringUtils from 'utils/String';

const RETRY_ATTEMPT = '{0} [GraphQL RetryMiddleware] Retry attempt {1}/{2} for {3} - Reason: {4}';
const RETRY_EXHAUSTED = '{0} [GraphQL RetryMiddleware] Retries exhausted for {1} - {2}';

const isJWTError = error => {
    if (error instanceof GraphQLException) {
        const errorMessage = error.message || '';
        const errorCause = error.cause || '';

        return (
            errorMessage.includes('valid JWT token') ||
            errorMessage.includes('JWT') ||
            errorCause.includes('valid JWT token') ||
            errorCause.includes('JWT')
        );
    }

    return false;
};

const isServerError = response => {
    return response && response.status >= 500 && response.status < 600;
};

const isAuthError = response => {
    return response && response.status === 401;
};

const isNetworkError = error => {
    return (
        (error instanceof TypeError && error.message === 'Network request failed') || (error instanceof Error && error.message === 'Failed to fetch')
    );
};

/**
 * RetryMiddleware handles retry logic for GraphQL requests on:
 * - Server errors (5xx responses)
 * - Network failures (fetch throws)
 * - JWT-related authentication errors
 * - 401 unauthorized responses
 *
 * Configuration:
 * - maxRetries: 3 retries (can be overridden via options.maxRetries)
 * - retryDelay: 2000ms (can be overridden via options.retryDelay)
 *
 * Creates fresh execution chains for each retry attempt.
 */
class RetryMiddleware {
    constructor() {
        this.MAX_RETRIES = 3;
        this.RETRY_DELAY = 2000;
    }

    async handleRetry(retryContext, attempt, reason) {
        const { operationName, maxAttempts, delay } = retryContext;

        Sephora.logger.info(stringUtils.format(RETRY_ATTEMPT, performance.now(), attempt + 1, maxAttempts - 1, operationName, reason));
        await new Promise(resolve => setTimeout(resolve, delay));
    }

    async executeDownstreamChain(context, middlewares, index) {
        if (index >= middlewares.length) {
            return null;
        }

        const nextFn = async () => this.executeDownstreamChain(context, middlewares, index + 1);

        return await middlewares[index].request(context, nextFn);
    }

    async request(context, next) {
        const { operationName, maxRetries, retryDelay } = context.options;
        const retriesAllowed = maxRetries ?? this.MAX_RETRIES;
        const maxAttempts = retriesAllowed + 1; // Total attempts = initial + retries
        const delay = retryDelay ?? this.RETRY_DELAY;

        // Get downstream middlewares for retry execution
        const retryIndex = context.middlewares.findIndex(m => m instanceof RetryMiddleware);
        const downstreamMiddlewares = context.middlewares.slice(retryIndex + 1);

        const retryContext = {
            context,
            next,
            downstreamMiddlewares,
            maxAttempts,
            delay,
            operationName
        };

        return this.attemptRequest(retryContext, 0);
    }

    async attemptRequest(retryContext, attempt) {
        const {
            context, next, downstreamMiddlewares, maxAttempts, operationName
        } = retryContext;

        try {
            // Execute downstream chain
            const result = attempt === 0 ? await next() : await this.executeDownstreamChain(context, downstreamMiddlewares, 0);

            // Check for retriable HTTP response errors
            if (isAuthError(result) || isServerError(result)) {
                if (attempt < maxAttempts - 1) {
                    const reason = isAuthError(result) ? 'Unauthorized' : 'Server Error';

                    await this.handleRetry(retryContext, attempt, reason);

                    return this.attemptRequest(retryContext, attempt + 1);
                }

                const reason = isAuthError(result) ? 'Unauthorized' : 'Server Error';

                Sephora.logger.error(stringUtils.format(RETRY_EXHAUSTED, performance.now(), operationName, `${reason} (${result.status})`));

                return result;
            }

            return result;
        } catch (error) {
            // Check for retriable errors
            if (isJWTError(error) || isNetworkError(error)) {
                if (attempt < maxAttempts - 1) {
                    const reason = isJWTError(error) ? 'JWT Error' : 'Network Error';

                    await this.handleRetry(retryContext, attempt, reason);

                    return this.attemptRequest(retryContext, attempt + 1);
                }

                const reason = isJWTError(error) ? 'JWT Error' : 'Network Error';

                Sephora.logger.error(stringUtils.format(RETRY_EXHAUSTED, performance.now(), operationName, `${reason}: ${error.message}`));
            }

            throw error;
        }
    }
}

export default RetryMiddleware;
